import React from 'react';
import clsx from 'clsx';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import { AutoSizer, Column, Table, TableCellRenderer, TableHeaderProps, IndexRange, OverscanIndexRange, CellMeasurer,CellMeasurerCache, RowMouseEventHandlerParams } from 'react-virtualized';
import _ from 'lodash';
import { scrollbarSize } from './scrollbarSize';

const styles = (theme: Theme) =>
  createStyles({
    flexContainer: {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },
    table: {
      // temporary right-to-left patch, waiting for
      // https://github.com/bvaughn/react-virtualized/issues/454
      '& .ReactVirtualized__Table__headerRow': {
        flip: false,
        paddingRight: theme.direction === 'rtl' ? '0 !important' : undefined,
      },
    },
    tableRow: {
      cursor: 'pointer',
    },
    tableRowHover: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
    },
    tableCell: {
      flex: 1,
      border: 'none',
    },
    noClick: {
      cursor: 'initial',
    },
  });

interface ColumnData {
  dataKey: string;
  label: string;
  numeric?: boolean;
  width: number;
  isClass?: boolean;
}

interface Row {
  index: number;
}

interface MuiVirtualizedTableProps extends WithStyles<typeof styles> {
  overscanRowCount?: number;
  columns: ColumnData[];
  headerHeight?: number;
  onRowClick?: (info: RowMouseEventHandlerParams) => void;
  rowCount: number;
  rowGetter: (row: Row) => any;
  rowHeight?: number;
  customCellRenderer?: TableCellRenderer;
  reference?: string | ((instance: Table | null) => void) | React.RefObject<Table> | null | undefined;
  onRowsRendered?:((info: IndexRange & OverscanIndexRange) => void) | undefined;
  scrollToIndex?: number;
  minWidth?: number;
  data?: any;
}

class MuiVirtualizedTable extends React.PureComponent<MuiVirtualizedTableProps> {
  static defaultProps = {
    headerHeight: 48,
    rowHeight: 48
  };

  getRowClassName = ({ index }: Row) => {
    const { classes, onRowClick } = this.props;

    return clsx(classes.tableRow, classes.flexContainer, {
      [classes.tableRowHover]: index !== -1 && onRowClick != null,
    });
  };

  cellRenderer: TableCellRenderer = (cProps) => {
    const { columns, classes, rowHeight, onRowClick, customCellRenderer } = this.props;
    return (
      <TableCell
        component="div"
        className={clsx(classes.tableCell, classes.flexContainer, {
          [classes.noClick]: onRowClick == null,
        })}
        variant="body"
        style={{ height: rowHeight, minWidth: columns[cProps.columnIndex].width + 'px'}}
        align={(cProps.columnIndex != null && columns[cProps.columnIndex].numeric) || false ? 'right' : 'left'}
      >
        {customCellRenderer ? customCellRenderer(cProps) : cProps.cellData}
      </TableCell>
    );
  };

  headerRenderer = ({ label, columnIndex }: TableHeaderProps & { columnIndex: number }) => {
    const { headerHeight, columns, classes } = this.props;

    return (
      <TableCell
        component="div"
        className={clsx(classes.tableCell, classes.flexContainer, classes.noClick)}
        variant="head"
        style={{ height: headerHeight, minWidth: columns[columnIndex].width + 'px'}}
        align={columns[columnIndex].numeric || false ? 'right' : 'left'}
      >
        <span>{label}</span>
      </TableCell>
    );
  };

  render() {
    const { data, classes, columns, rowHeight, headerHeight,reference, ...tableProps } = this.props;
    return (
      <AutoSizer>
        {({ height, width }) => {
          // narrower than the table's minimum: scroll sideways inside the space we were given, and leave room for that
          // scrollbar, so the table never grows past its parent (which made the parent scroll and the size flicker)
          const scrollX = !!tableProps.minWidth && width < tableProps.minWidth;
          return (
          <div style={{width, height, overflowX: scrollX ? 'auto' : 'hidden', overflowY: 'hidden'}}>
          <Table
            height={scrollX ? Math.max(0, height - scrollbarSize()) : height}
            width={scrollX ? tableProps.minWidth! : width}
            rowHeight={rowHeight!}
            gridStyle={{
              direction: 'inherit',
            }}
            headerHeight={headerHeight!}
            className={classes.table}
            ref={reference}
            {...tableProps}
            rowClassName={this.getRowClassName}
            data={data}
          >
            {columns.map(({ dataKey, ...other }, index) => {
              return (
                <Column
                  key={dataKey}
                  headerRenderer={(headerProps) =>
                    this.headerRenderer({
                      ...headerProps,
                      columnIndex: index,
                    })
                  }
                  className={classes.flexContainer}
                  cellRenderer={this.cellRenderer}
                  dataKey={dataKey}
                  cellDataGetter={({rowData,dataKey})=>{
                    return _.get(rowData,dataKey);
                  }}
                  {...other}
                />
              );
            })}
          </Table>
          </div>
        )}}
      </AutoSizer>
    );
  }
}

export const VirtualizedTable = withStyles(styles)(MuiVirtualizedTable);